import { Badge } from '@/components/ui/Badge';
import { EVENTS_STRINGS } from '../constants/events-strings';

export interface FeaturedEventBadgeProps {
  /**
   * `banner` = the full-width featured banner on the authenticated events page
   * (Figma 418:15515). `card` = the badge overlaid on the first grid card in
   * the public events section (Figma I418:15577;418:16798). Same colour pair,
   * different padding and weight - Figma pads the banner badge wider and sets
   * it Bold, the card badge tighter and SemiBold.
   */
  size?: 'banner' | 'card';
  className?: string;
}

/*
 * Brand yellow arrives via `Badge`'s `warning` variant (`bg-yellow text-navy`),
 * the semantic token pair for `#FED13D` on `#001928` - the literal hex is never
 * written here, per FR-35's "via a semantic token, never as a literal" and
 * ui-guidelines §9 (Warning/Pending).
 *
 * Only the GEOMETRY is restated. `Badge`'s `sm` chip is 10px text on an 8px
 * radius, and both Figma nodes above are 11px on a 4px radius (§9's "Standard
 * badge: border-radius 4px"), so radius, padding, size, and weight come from
 * the design rather than being rounded to the primitive's defaults.
 *
 * `!` is load-bearing, not decoration: these utilities collide with the ones
 * `Badge` emits for `size="sm"` (`rounded-lg px-2.5 py-0.5 text-[10px]`) and its
 * base `font-semibold`. Tailwind gives colliding utilities equal specificity, so
 * without `!` which one applies depends on stylesheet emit order rather than on
 * class-attribute order - a silent, build-dependent regression. Held as one
 * constant per size so the two call sites cannot drift.
 */
const SIZE_CLASSES: Record<NonNullable<FeaturedEventBadgeProps['size']>, string> = {
  // Figma 418:15515: px 10px, py 6px, radius 4px, Poppins Bold 11px, uppercase.
  banner: 'rounded-[0.25rem]! px-2.5! py-1.5! text-[11px]! font-bold! uppercase',
  // Figma I418:15577;418:16798: px 8px, py 4px, radius 4px, Poppins SemiBold 11px.
  card: 'rounded-[0.25rem]! px-2! py-1! text-[11px]! font-semibold! uppercase',
};

/**
 * The `#FED13D` FEATURED EVENT marker (FR-35). Rendered by both member-facing
 * surfaces - the authenticated banner and the first card of the public events
 * section - so the two cannot disagree about what "featured" looks like.
 */
export function FeaturedEventBadge({ size = 'banner', className = '' }: FeaturedEventBadgeProps) {
  return (
    <Badge variant="warning" size="sm" className={`${SIZE_CLASSES[size]} ${className}`.trim()}>
      {EVENTS_STRINGS.FEATURED_BADGE}
    </Badge>
  );
}
